import type {
  DocumentData,
  DocumentSnapshot,
  FieldValue,
} from "firebase/firestore";
import { makeObservable } from "mobx";
import { DbInfoType } from "../dev/DevsStore";
import { log } from "../ui/log";
import type { Api } from "./apis";
import { firestoreApi } from "./apis/firestoreApi";
import type { BaseModel, BaseModelData } from "./BaseModel";
import type { BaseItemStore } from "./BaseStore";
import type { Stores } from "./stores";
import { trackEvent } from "./trackEvent";

export default abstract class BaseApi<
  Model extends BaseModel<BaseModelData>,
  ModelData,
> {
  protected ongoing: Set<string> = new Set();

  private createCount = 0;
  private addCount = 0;
  private updateCount = 0;
  private setCount = 0;
  private deleteCount = 0;

  private resetCounters() {
    this.createCount =
      this.addCount =
      this.updateCount =
      this.setCount =
      this.deleteCount =
        0;
  }

  private setTick() {
    this.setCount++;
    this.checkTicks();
  }

  private updateTick() {
    this.updateCount++;
    this.checkTicks();
  }

  private addTick() {
    this.addCount++;
    this.checkTicks();
  }

  private createTick() {
    this.createCount++;
    this.checkTicks();
  }

  private deleteTick() {
    this.deleteCount++;
    this.checkTicks();
  }

  private checkTicks() {
    if (
      this.createCount +
        this.addCount +
        this.updateCount +
        this.setCount +
        this.deleteCount >=
      100
    ) {
      this.stores.devsStore.gotError = new Error(
        `DB_USAGE_ERROR_${this.addCount}_${this.updateCount}_${this.setCount}_${this.deleteCount}_${this.db}`,
      );
    }
  }

  constructor(
    protected api: Api,
    protected stores: Stores,
  ) {
    this.setupEvents();

    makeObservable(this);

    setInterval(() => {
      this.resetCounters();
    }, 1000);
  }

  setupEvents() {}

  get dbPrefix() {
    const isStaging = window?.location?.host.includes("--staging");

    return __DEV__ ? "dev_" : isStaging ? "staging_" : "prod_";
  }

  async getById(uid: string) {
    try {
      this.stores.devsStore.addDbStats(DbInfoType.Get, this.db, uid);

      this.ongoing.add(uid);

      log.network("Get by ID from", this.db, "uid", uid);

      const docSnap = await firestoreApi.getDocument(this.db, uid);

      if (docSnap) {
        const itemModel = this.fromFirestore(docSnap);

        this.store.load([itemModel]);
        this.ongoing.delete(uid);
        return itemModel;
      }

      this.ongoing.delete(uid);

      return;
    } catch (e: unknown) {
      log.error("Got getById error", e);

      if (e instanceof Error) {
        const errorWithMessage = new Error(
          `${e.message} -  Get by ID from ${this.db}, uid: ${uid}`,
        );
        errorWithMessage.name = e.name;
        errorWithMessage.stack = e.stack;

        this.stores.devsStore.setGotError(errorWithMessage);
      }
    }
  }

  async add(item: Omit<ModelData, "uid">, addTick = true): Promise<Model> {
    if (addTick) {
      // Skipped in copy()
      this.addTick();
    }

    if (__DEV__) {
      log.network(`Add BaseApi db ${this.db} data:`, item);
    }

    const docRef = await firestoreApi.addDocument(this.db, item);

    /*
      From mr Internet.
      Additionally, adding a getDoc immediately after isn't as network expensive as you think it is.
      If your application uses that document anywhere in your application (either listening to the
      document itself or its parent collection), your client app will be notified and given the updated
      data to store in its local cache regardless of if you need it immediately after adding it.
    */
    const snapshotWithServerTimestamps = await firestoreApi.getDocument(
      this.db,
      docRef.id,
    );

    const itemModel = this.fromFirestore({
      data: () => ({ ...snapshotWithServerTimestamps.data(), uid: docRef.id }),
    });

    this.stores.devsStore.addDbStats(DbInfoType.Add, this.db, docRef.id);
    this.stores.devsStore.addDbStats(DbInfoType.Get, this.db, docRef.id); // for timestamps

    log.network("Storing locally with data", this.db, itemModel);
    this.store.load([itemModel]);

    trackEvent(`add_in_${this.db}`);

    return itemModel;
  }

  async create(uid: string, itemData: Omit<ModelData, "uid">): Promise<Model> {
    this.createTick();

    if (__DEV__) {
      log.network(`Create db ${this.db} data:`, itemData);
    }

    await firestoreApi.setDocument(this.db, uid, itemData);

    const snapshotWithServerTimestamps = await firestoreApi.getDocument(
      this.db,
      uid,
    );

    const itemModel = this.fromFirestore({
      data: () => ({ ...snapshotWithServerTimestamps.data(), uid }),
    });

    this.stores.devsStore.addDbStats(DbInfoType.Set, this.db, uid);
    this.stores.devsStore.addDbStats(DbInfoType.Get, this.db, uid); // for timestamps

    log.network("Storing locally with data", this.db, itemModel);
    this.store.load([itemModel]);

    trackEvent(`create_in_${this.db}`);

    return itemModel;
  }

  async set(item: Model) {
    this.setTick();

    // Resave locally
    this.store.load([item]);

    const itemData = item.toFireStore();

    if (__DEV__) {
      log.network(`Set db ${this.db} uid ${item.uid}, data:`, itemData);
    }

    await firestoreApi.setDocument(this.db, item.uid, itemData);

    const snapshotWithServerTimestamps = await firestoreApi.getDocument(
      this.db,
      item.uid,
    );

    const itemModel = this.fromFirestore({
      data: () => ({ ...snapshotWithServerTimestamps.data(), uid: item.uid }),
    });

    this.stores.devsStore.addDbStats(DbInfoType.Set, this.db, item.uid);
    this.stores.devsStore.addDbStats(DbInfoType.Get, this.db, item.uid); // for timestamps

    // Resave locally
    this.store.load([itemModel]);

    return itemModel;
  }

  async update(
    uid: string,
    itemData:
      | Partial<ModelData>
      | Partial<{ [key in keyof ModelData]: FieldValue }>,
  ) {
    this.updateTick();

    this.stores.devsStore.addDbStats(DbInfoType.Update, this.db, uid);
    this.stores.devsStore.addDbStats(DbInfoType.Get, this.db, uid); // for timestamps

    if (__DEV__) {
      log.network(`Update db ${this.db} uid ${uid}, data:`, itemData);
    }

    await firestoreApi.updateDocument(this.db, uid, itemData);

    /*
      From mr Internet.
      Additionally, adding a getDoc immediately after isn't as network expensive as you think it is. If your application uses that document anywhere in your application (either listening to the document itself or its parent collection), your client app will be notified and given the updated data to store in its local cache regardless of if you need it immediately after adding it.
    */
    const snapshotWithServerTimestamps = await firestoreApi.getDocument(
      this.db,
      uid,
    );

    const itemModel = this.fromFirestore({
      data: () => ({ ...snapshotWithServerTimestamps.data(), uid: uid }),
    });

    // Resave locally
    this.store.load([itemModel]);

    return itemModel;
  }

  async delete(item: { uid: string }) {
    this.deleteTick();

    const uid = item.uid;

    if (__DEV__) {
      log.network(`Delete from db ${this.db} uid ${item.uid}`);
    }

    await firestoreApi.deleteDocument(this.db, uid);

    // Also delete it locally
    this.store.deleteItem(uid);
  }

  watchItem(uid: string) {
    try {
      this.stores.devsStore.addDbStats(DbInfoType.WatchAdded, this.db, uid);
      if (__DEV__) {
        log.network(`Started watchItem from db ${this.db}, uid: ${uid}`);
      }

      const unsub = firestoreApi.subscribeToDocument(
        this.db,
        uid,
        (doc: DocumentSnapshot<DocumentData, DocumentData>) => {
          if (doc.metadata.hasPendingWrites) {
            log.network("Skipping one load in watcher", {
              doc,
              data: doc.data(),
            });
            return;
          }

          this.stores.devsStore.addDbStats(
            DbInfoType.WatchItem,
            this.db,
            doc.id,
          );

          if (doc.exists()) {
            if (__DEV__) {
              log.network("Got watchItem updated dataUid", doc.id);
            }
            this.store.load([
              this.fromFirestore(doc, this.store.byId.get(doc.id)),
            ]);
          } else {
            if (__DEV__) {
              log.network("Got watchItem deleted dataUid", doc.id);
            }
            this.store.deleteItem(doc.id);
          }
        },
      );

      return () => {
        this.stores.devsStore.addDbStats(DbInfoType.WatchRemoved, this.db, uid);
        unsub();
      };
    } catch (e: unknown) {
      log.error("Got watchItem error", e);

      if (e instanceof Error) {
        const errorWithMessage = new Error(
          `${e.message} - Started watchItem from db ${this.db}, uid: ${uid}`,
        );
        errorWithMessage.name = e.name;
        errorWithMessage.stack = e.stack;

        this.stores.devsStore.setGotError(errorWithMessage);
      }

      return () => {};
    }
  }

  toFirestore(item: Model) {
    return item.toFireStore();
  }

  abstract get db(): string;
  abstract get store(): BaseItemStore<Model>;
  protected abstract fromFirestore(
    res: DocumentData,
    previousItemData?: Model,
  ): Model;
}
